import { useEffect } from 'react';
import { beatLevel, useMusic } from '../store/music.js';

interface Dancer {
  el: HTMLElement;
  /** Оригінал кнопки: ховаємо, поки його копія в строю, і по ньому ж повертаємось. */
  origin: HTMLElement | null;
  w: number;
  h: number;
  /** Розмір оригіналу: з нього кнопка виростає в солдата й до нього повертається. */
  homeW: number;
  homeH: number;
  /** Звідки вийшла (центр) і коли вирушає. */
  home: { x: number; y: number };
  leaveAt: number;
  /** Де кнопка зараз у строю: за цим місцем вона плавно дотягується до свого, коли стрій перешиковується. */
  cur: { x: number; y: number } | null;
}

const LEAVE = 0.12; // секунд між виходами зі своїх місць
const TRAVEL = 0.7; // секунд, поки кнопка добирається до строю
const RETURN = 1.1; // секунд на повернення: кнопка встає на місце й поступово стає собою
const MAX = 160;
/** Крок між солдатами в шерензі та відступ від прапороносця. */
const COL_GAP = 22;
const LEAD_GAP = 120; // прапороносець іде з невеликим відривом від строю
const SPEED = 70; // px/с
/** Нахил назад, як у стройовому кроці: голова відстає від ніг. */
const LEAN = -0.12;
const UPRIGHT = -Math.PI / 2;

const ease = (v: number): number => {
  const k = Math.min(1, Math.max(0, v));
  return k * k * (3 - 2 * k);
};

/** Усі кнопки, що зараз видно на екрані: вони й ідуть у стрій. */
/**
 * Що для людини є кнопкою: справжні кнопки й посилання, перемикачі, вкладки,
 * поля вибору, а ще те, що лише виглядає як кнопка, — «Додати свій» (label з
 * вибором файлу), «Власний» колір (span із прихованим полем), розкривні заголовки.
 */
const BUTTONS = [
  'button',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  'select',
  '[role="button"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="option"]',
  'label.btn',
  'label.fb-pattern',
  'span.accent',
  'summary',
].join(', ');

/** Усі кнопки, що зараз видно на екрані: вони й ідуть у стрій. */
function screenButtons(): HTMLElement[] {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const found = new Set(document.querySelectorAll<HTMLElement>(BUTTONS));
  return [...found].filter(el => {
    if (el.closest('.conga-layer') || el.classList.contains('player-power')) return false;
    // Кнопка в кнопці (посилання з кнопкою всередині) — іде одна, зовнішня.
    const outer = el.parentElement?.closest<HTMLElement>(BUTTONS);
    if (outer && found.has(outer)) return false;
    const r = el.getBoundingClientRect();
    if (r.width < 6 || r.height < 6 || r.right < 0 || r.bottom < 0 || r.left > vw || r.top > vh) return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && Number(cs.opacity) > 0.05;
  });
}

const labelOf = (el: HTMLElement): string => {
  const text =
    el instanceof HTMLSelectElement
      ? el.selectedOptions[0]?.textContent
      : el.getAttribute('aria-label') || el.title || el.textContent;
  return (text ?? '').trim().slice(0, 18) || '•';
};

/**
 * Парад кнопок: коли грає гімн, усі кнопки на екрані по черзі покидають свої
 * місця, стають сторч (як солдати, у шоломах і з нахилом назад) і шикуються
 * по ранжиру — від найвищої до найнижчої, — а «Прибрати потужність» із прапором
 * України іде попереду. Стрій марширує зліва направо й заходить знову. Усі підстрибують
 * і гойдаються в такт басу. Вимкнув музику — кожна повертається на місце.
 *
 * Це копії кнопок у верхньому шарі: справжні лишаються на місцях (сховані),
 * тож розкладка сторінки не стрибає. Клікабельна лише головна — вона вимикає музику.
 */
export function Conga(): null {
  const playing = useMusic(s => s.playing);

  useEffect(() => {
    if (!playing || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const layer = document.createElement('div');
    layer.className = 'conga-layer';
    document.body.appendChild(layer);

    const make = (label: string, cls: string): HTMLElement => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `btn conga-dancer ${cls}`;
      // Підпис окремо: коли кнопка стискається до розміру оригіналу, він обрізається, а не вилазить.
      const text = document.createElement('span');
      text.className = 'conga-label';
      text.textContent = label;
      el.appendChild(text);
      el.tabIndex = -1;
      // Шолом на «голові» — на кінці кнопки, що після повороту стає верхом.
      const helmet = document.createElement('span');
      helmet.className = 'conga-helmet';
      helmet.setAttribute('aria-hidden', 'true');
      el.appendChild(helmet);
      layer.appendChild(el);
      return el;
    };
    /** Розмір солдата — як у звичайної кнопки з усіма мінімумами; після виміру мінімуми знімаємо, щоб кнопка могла стиснутись до оригіналу. */
    const measure = (el: HTMLElement): { w: number; h: number } => {
      const size = { w: el.offsetWidth, h: el.offsetHeight };
      el.style.minWidth = '0';
      el.style.minHeight = '0';
      return size;
    };

    /** Вигляд оригіналу на копії: з нього кнопка перетікає в солдата й до нього повертається. */
    const looksLike = (el: HTMLElement, origin: HTMLElement | null, instant = false): void => {
      // На старті без переходу: копія одразу має бути схожа на оригінал.
      const parts = instant ? [el, ...el.querySelectorAll<HTMLElement>('.conga-helmet, .conga-flag, .conga-cap')] : [];
      parts.forEach(p => (p.style.transition = 'none'));
      el.classList.add('conga-home');
      if (origin) {
        const cs = getComputedStyle(origin);
        el.style.background = cs.backgroundColor;
        el.style.color = cs.color;
        el.style.borderRadius = cs.borderRadius;
        el.style.boxShadow = cs.boxShadow;
      }
      if (instant) {
        void el.offsetWidth;
        parts.forEach(p => (p.style.transition = ''));
      }
    };
    /** Зняти вигляд оригіналу: CSS-переходи самі доведуть кнопку до солдата. */
    const enlist = (el: HTMLElement): void => {
      el.classList.remove('conga-home');
      el.style.background = '';
      el.style.color = '';
      el.style.borderRadius = '';
      el.style.boxShadow = '';
    };

    // Ведучий: справжня кнопка плитки, якщо вона на екрані, інакше — з кутка.
    const realLeader = document.querySelector<HTMLElement>('.player-power');
    const leaderEl = make('Прибрати потужність', 'primary conga-leader');
    leaderEl.tabIndex = 0;
    // Ведучий — генерал: замість шолома кашкет з кокардою й золотим шиттям на козирку.
    leaderEl.querySelector('.conga-helmet')?.remove();
    const cap = document.createElement('span');
    cap.className = 'conga-cap';
    cap.setAttribute('aria-hidden', 'true');
    cap.innerHTML = '<i class="crown"></i><i class="band"></i><i class="visor"></i><i class="badge"></i>';
    leaderEl.appendChild(cap);
    // Прапор України: древко вздовж прапороносця, вище шолома.
    const flag = document.createElement('span');
    flag.className = 'conga-flag';
    flag.setAttribute('aria-hidden', 'true');
    flag.innerHTML = '<i></i><b></b>';
    leaderEl.appendChild(flag);
    leaderEl.addEventListener('click', () => useMusic.getState().setOn(false));
    const lSize = measure(leaderEl);
    const lRect = realLeader?.getBoundingClientRect();
    const start = performance.now();
    const leader = {
      w: lSize.w,
      h: lSize.h,
      homeW: lRect?.width ?? lSize.w,
      homeH: lRect?.height ?? lSize.h,
      home: lRect
        ? { x: lRect.left + lRect.width / 2, y: lRect.top + lRect.height / 2 }
        : { x: window.innerWidth - lSize.w, y: window.innerHeight - 40 },
    };
    if (realLeader) realLeader.style.visibility = 'hidden';
    // Шикування теж плавне: копія спершу виглядає як оригінал, а вирушаючи,
    // поступово стає солдатом — колір, обрис, шолом.
    const timers: number[] = [];
    if (realLeader) looksLike(leaderEl, realLeader, true);
    timers.push(window.setTimeout(() => enlist(leaderEl), 60));

    // Стрій: кожна кнопка виходить зі свого місця по черзі.
    const dancers: Dancer[] = [];
    const taken = new Set<HTMLElement>();
    const recruit = (origin: HTMLElement, leaveAt: number): void => {
      if (taken.has(origin) || dancers.length >= MAX) return;
      taken.add(origin);
      const el = make(labelOf(origin), dancers.length % 2 ? 'conga-yellow' : 'conga-blue');
      const r = origin.getBoundingClientRect();
      origin.style.visibility = 'hidden';
      const size = measure(el);
      looksLike(el, origin, true);
      timers.push(window.setTimeout(() => enlist(el), leaveAt - performance.now()));
      dancers.push({
        el,
        origin,
        ...size,
        homeW: r.width,
        homeH: r.height,
        home: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
        leaveAt,
        cur: null,
      });
    };
    screenButtons().forEach((origin, k) => recruit(origin, start + 400 + k * LEAVE * 1000));

    // Стрій: шеренги одна над одною, у кожній солдати стоять сторч і йдуть
    // за прапороносцем. Висота солдата — ширина кнопки, товщина — її висота.
    // Перераховується, коли до строю приєднуються нові кнопки.
    let tallest = 0;
    let rowH = 0;
    let rows = 1;
    let colW = 0;
    let length = 0;
    const relayout = (): void => {
      // По ранжиру: найвищі (найдовші кнопки) — першими за прапороносцем.
      dancers.sort((a, b) => b.w - a.w);
      tallest = Math.min(240, Math.max(leader.w, ...dancers.map(d => d.w)));
      rowH = tallest + 34;
      rows = Math.max(1, Math.min(3, Math.floor((window.innerHeight - 120) / rowH)));
      const thick = Math.max(leader.h, ...dancers.map(d => d.h));
      colW = thick + COL_GAP;
      length = LEAD_GAP + Math.ceil(dancers.length / rows) * colW + thick;
    };
    relayout();
    const baseOf = (row: number, cy: number): number => cy + (row - (rows - 1) / 2) * rowH + tallest / 2;
    const slotOf = (i: number, front: number, cy: number): { x: number; y: number } => {
      const col = Math.floor(i / rows);
      const row = i % rows;
      return { x: front - LEAD_GAP - col * colW, y: baseOf(row, cy) - dancers[i]!.w / 2 };
    };

    // Прокрутили сторінку — нові кнопки на екрані доганяють стрій і стають у нього.
    let joinTimer = 0;
    const join = (): void => {
      joinTimer = 0;
      if (stoppedAt) return;
      const now = performance.now();
      const fresh = screenButtons().filter(o => !taken.has(o));
      if (!fresh.length) return;
      fresh.forEach((origin, k) => recruit(origin, now + 80 + k * LEAVE * 1000));
      relayout();
    };
    const onScroll = (): void => {
      if (!joinTimer) joinTimer = window.setTimeout(join, 150);
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    // Нові кнопки бувають і без прокрутки: відкрилась інша сторінка чи блок.
    const poll = window.setInterval(join, 1500);

    let raf = 0;
    let stoppedAt = 0;
    let side = false;
    let lastBeat = 0;
    /**
     * `soldier` — наскільки кнопка вже солдат: 0 — звичайна кнопка на своєму
     * місці, 1 — стоїть сторч у строю. На виході й поверненні він плавно
     * змінюється, тож кнопка не перекидається ривком.
     */
    const place = (
      el: HTMLElement,
      size: { w: number; h: number; homeW: number; homeH: number },
      x: number, y: number, k: number, now: number, soldier: number,
    ): void => {
      // Форма й розмір теж перетікають: від оригіналу до солдата і назад.
      const w = size.homeW + (size.w - size.homeW) * soldier;
      const h = size.homeH + (size.h - size.homeH) * soldier;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      // Крок: усі ступають разом — ліва, права; на удар — підскок.
      const step = Math.sin(now / 240) * (k % 2 ? 1 : -1);
      const bounce = (-beatLevel * 16 - Math.abs(Math.sin(now / 240)) * 4) * soldier;
      const sway = (step * 0.05 + (side ? 1 : -1) * beatLevel * 0.12) * soldier;
      const scale = 1 + beatLevel * 0.18 * soldier;
      const turn = (UPRIGHT + LEAN) * soldier + sway;
      el.style.transform = `translate(${x - w / 2}px, ${y - h / 2 + bounce}px) rotate(${turn}rad) scale(${scale})`;
    };

    let frontPos = window.innerWidth * 0.55;
    let wrapped = false;
    let lastNow = start;
    const frame = (now: number): void => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, Math.max(0, (now - lastNow) / 1000));
      lastNow = now;
      if (beatLevel > 0.85 && now - lastBeat > 120) {
        lastBeat = now;
        side = document.documentElement.dataset.beat === 'blue';
      }
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Голова строю: стартує посередині, марширує праворуч і заходить зліва знову.
      // Положення накопичуємо, а не рахуємо з часу: довжина строю росте, коли
      // приєднуються нові кнопки, і стрій від цього не має стрибати.
      const span = w + length + 120;
      frontPos += SPEED * dt;
      if (frontPos >= span) {
        frontPos -= span;
        // Перший прохід хвіст ще підтягується зліва; переносити його можна лише після першого кола.
        wrapped = true;
      }
      const front = frontPos - 40;
      const follow = 1 - Math.exp(-dt * 9);
      const cy = h / 2;

      const out = ease((now - start) / 900);
      const back = stoppedAt ? ease((now - stoppedAt) / (RETURN * 1000)) : 0;
      const ty = baseOf((rows - 1) / 2, cy) - leader.w / 2;
      let lx = leader.home.x + (front - leader.home.x) * out;
      let ly = leader.home.y + (ty - leader.home.y) * out;
      lx += (leader.home.x - lx) * back;
      ly += (leader.home.y - ly) * back;
      place(leaderEl, leader, lx, ly, 0, now, out * (1 - back));

      dancers.forEach((d, i) => {
        const slot = slotOf(i, front, cy);
        // Хвіст, що лишився праворуч після переходу голови, дотягується, а не стрибає.
        if (wrapped && slot.x < -colW) slot.x += span;
        const go = ease((now - d.leaveAt) / (TRAVEL * 1000));
        let x = d.home.x + (slot.x - d.home.x) * go;
        let y = d.home.y + (slot.y - d.home.y) * go;
        // Перешикування (хтось новий став за ранжиром) — плавно до нового місця, а не стрибком.
        if (!stoppedAt && go >= 1 && d.cur && Math.abs(x - d.cur.x) < w * 0.5) {
          x = d.cur.x + (x - d.cur.x) * follow;
          y = d.cur.y + (y - d.cur.y) * follow;
        }
        d.cur = { x, y };
        let r = 0;
        if (stoppedAt) {
          r = ease((now - stoppedAt - i * 30) / (RETURN * 1000));
          x += (d.home.x - x) * r;
          y += (d.home.y - y) * r;
        }
        place(d.el, d, x, y, i + 1, now, go * (1 - r));
      });
    };
    raf = requestAnimationFrame(frame);

    const restore = (): void => {
      cancelAnimationFrame(raf);
      timers.forEach(id => window.clearTimeout(id));
      window.clearTimeout(joinTimer);
      window.clearInterval(poll);
      window.removeEventListener('scroll', onScroll, { capture: true });
      layer.remove();
      if (realLeader) realLeader.style.visibility = '';
      dancers.forEach(d => {
        if (d.origin) d.origin.style.visibility = '';
      });
    };

    /**
     * Перетікання в справжню кнопку: копія бере фон, колір і обрис оригіналу,
     * знімає шолом і прапор, а наприкінці оригінал проступає під нею, і копія
     * тане. Підміни не видно.
     */
    const morphHome = (el: HTMLElement, origin: HTMLElement | null): void => {
      looksLike(el, origin);
      if (!origin) el.style.opacity = '0';
    };
    const finish = (): void => {
      if (realLeader) realLeader.style.visibility = '';
      dancers.forEach(d => {
        if (d.origin) d.origin.style.visibility = '';
      });
      layer.classList.add('conga-fade');
      window.setTimeout(restore, 220);
    };
    // Музику вимкнули — кнопки повертаються додому, і лише тоді ми прибираємо шар.
    const unsub = useMusic.subscribe(s => {
      if (s.playing || stoppedAt) return;
      stoppedAt = performance.now();
      unsub();
      timers.forEach(id => window.clearTimeout(id));
      window.clearTimeout(joinTimer);
      window.clearInterval(poll);
      // Сторінку могли прокрутити: додому — туди, де оригінал зараз.
      const homeOf = (o: HTMLElement | null, into: { home: { x: number; y: number }; homeW: number; homeH: number }) => {
        if (!o?.isConnected) return false;
        const r = o.getBoundingClientRect();
        into.home = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
        into.homeW = r.width;
        into.homeH = r.height;
        return true;
      };
      morphHome(leaderEl, homeOf(realLeader, leader) ? realLeader : null);
      dancers.forEach(d => morphHome(d.el, homeOf(d.origin, d) ? d.origin : null));
      window.setTimeout(finish, RETURN * 1000 + dancers.length * 30 + 60);
    });
    return () => {
      unsub();
      // Музику вимкнули: кнопки ще повертаються додому, шар прибере таймер.
      if (!stoppedAt) restore();
    };
  }, [playing]);

  return null;
}
